import { bool, string, object, func } from 'prop-types';
import { useState, useEffect, useContext } from 'react';
import { QueryRenderer, graphql } from 'react-relay/legacy';
import cn from 'classnames';

import { stepInteractionConstants } from 'dibs-tracking';
const { STEP_INTERACTION_GLOBAL_MESSAGE_BANNER_CLOSE } = stepInteractionConstants;
import Close from 'dibs-icons/exports/legacy/Close';
import { Link } from 'dibs-elements/exports/Link';
import { getGeoLocationPromoGroupFromLocalStorage } from 'dibs-display-promo-code/exports/promoHelpers';

import { HeaderFooterServerVarsContext } from '../HeaderFooterServerVarsContext/HeaderFooterServerVarsContext';
import MessageBannerContent from './MessageBannerContent';
import { getIsFirstTimeVisitor } from './helpers/messageHelpers';
import { isPreview, getPreview, getUserTypeAndSetIdFromUrl } from './helpers/previewHelpers';
import {
    trackMessageBanner,
    isMessageBannerClosedManually,
    setIsBannerClosedManuallyInStorage,
    hasAnyMessagesByDevice,
} from './helpers/sharedMessageHelpers';

import styles from './MessageBanner-style.scss';

/**
 * Messages for marketing banner can come from multiple sources and have the following precedence:
 * (1) Preview messages that pulled from a stringified object on a URL query param
 * (2) Preview messages that are fetched with userType and/or setId from the URL
 * (3) Registered user messages that are fetched with userId
 * (4) Unregistered user messages that are fetched if user is not logged in
 */
function MessageBanner(props) {
    const [variables, setVariables] = useState({
        fireMessageBannerRefetch: false,
        isFirstTimeVisitor: false,
        hasRegisteredUserMessages: false,
        hasUnregisteredUserMessages: false,
        hasPreviewVariables: false,
        previewSetId: '',
        previewUserType: '',
        isGeoLocationPromoGroupSet: !!getGeoLocationPromoGroupFromLocalStorage().length,
    });
    const { isMobile } = useContext(HeaderFooterServerVarsContext);
    const [previewMessagesFromUrl, setPreviewMessagesFromUrl] = useState(null);
    const [isBannerClosedManually, setIsBannerClosedManually] = useState(false);
    const [currentMessageTitle, setCurrentMessageTitle] = useState('');
    useEffect(() => {
        const { messages: previewMessages } = getPreview();
        if (previewMessages) {
            setPreviewMessagesFromUrl(previewMessages);
        }
    }, []);
    const {
        isClient,
        hasRootPendingRefetch,
        userId,
        hasUserId,
        useGlobalFallbackMessage = false,
        relayEnvironment,
        onBannerClose = () => {},
        unsetMobileHeight,
    } = props;
    useEffect(() => {
        if (
            !variables.fireMessageBannerRefetch &&
            isClient &&
            !previewMessagesFromUrl &&
            !hasRootPendingRefetch
        ) {
            let nextVariables = {
                ...variables,
                userId,
                fireMessageBannerRefetch: true,
                isFirstTimeVisitor: !hasUserId && getIsFirstTimeVisitor(),
                hasRegisteredUserMessages: hasUserId,
                hasUnregisteredUserMessages: !hasUserId,
                hasPreviewVariables: false,
                previewSetId: '',
                previewUserType: '',
            };

            const { userType: previewUserType, setId: previewSetId } =
                getUserTypeAndSetIdFromUrl() || {};

            if (previewUserType && previewSetId) {
                nextVariables = {
                    ...nextVariables,
                    hasPreviewVariables: true,
                    previewSetId,
                    previewUserType,
                };
            }
            setVariables(nextVariables);
        }
    }, [hasRootPendingRefetch, isClient, variables, previewMessagesFromUrl, userId, hasUserId]);

    useEffect(() => {
        if (isClient) {
            setIsBannerClosedManually(isMessageBannerClosedManually());
        }
    }, [isClient]);

    if (
        !isClient ||
        //Disable banner close on desktop and tablet
        (isBannerClosedManually && isMobile) ||
        (!variables.fireMessageBannerRefetch && !useGlobalFallbackMessage)
    ) {
        return null;
    }

    if (!variables.fireMessageBannerRefetch && !previewMessagesFromUrl) {
        return isMobile ? null : <div className={styles.bannerContainer} />;
    }

    if (previewMessagesFromUrl) {
        return (
            <div
                className={cn(styles.bannerContainer, {
                    [styles.unsetMobileHeight]: unsetMobileHeight,
                })}
            >
                <MessageBannerContent
                    viewer={null}
                    user={null}
                    messages={null}
                    showPreviewMessage
                    messagesToPreview={previewMessagesFromUrl}
                />
            </div>
        );
    }

    return (
        <QueryRenderer
            environment={relayEnvironment}
            variables={variables}
            query={graphql`
                query MessageBannerQuery(
                    $userId: String!
                    $hasRegisteredUserMessages: Boolean!
                    $hasUnregisteredUserMessages: Boolean!
                    $isFirstTimeVisitor: Boolean!
                    $hasPreviewVariables: Boolean!
                    $previewSetId: String!
                    $previewUserType: String!
                    $isGeoLocationPromoGroupSet: Boolean!
                ) {
                    viewer {
                        ...MessageBannerContent_viewer
                        activePromotion(userId: $userId) {
                            ...sharedMessageHelpers_activePromotion
                        }
                        expiringPromotion(userId: $userId) {
                            ...sharedMessageHelpers_expiringPromotion
                        }
                        user(userId: $userId) @include(if: $hasRegisteredUserMessages) {
                            ...MessageBannerContent_user
                            registeredUserMessages: bannerMessages {
                                ...MessageBannerContent_messages
                                ...sharedMessageHelpers_messages
                            }
                        }
                        unregisteredUserMessages: messageSet(
                            isFirstTimeVisitor: $isFirstTimeVisitor
                        ) @include(if: $hasUnregisteredUserMessages) {
                            ...MessageBannerContent_messages
                            ...sharedMessageHelpers_messages
                        }
                        previewMessages: messageSet(
                            setId: $previewSetId
                            userType: $previewUserType
                        ) @include(if: $hasPreviewVariables) {
                            ...MessageBannerContent_messages
                            ...sharedMessageHelpers_messages
                        }
                    }
                }
            `}
            render={({ error, props: renderProps }) => {
                if (error) {
                    return null;
                } else if (!renderProps) {
                    if (isMobile) {
                        return null;
                    } else {
                        return useGlobalFallbackMessage ? (
                            <div
                                className={cn(styles.bannerContainer, {
                                    [styles.unsetMobileHeight]: unsetMobileHeight,
                                })}
                            />
                        ) : null;
                    }
                }

                const { viewer } = renderProps;
                const { user, activePromotion, expiringPromotion } = viewer;
                let messages = viewer?.previewMessages || null;

                if (!isPreview()) {
                    messages =
                        user?.registeredUserMessages || viewer?.unregisteredUserMessages || null;
                }

                if (
                    !useGlobalFallbackMessage &&
                    !hasAnyMessagesByDevice(messages, activePromotion, expiringPromotion)
                ) {
                    return null;
                }

                return (
                    <div
                        className={cn(styles.bannerContainer, {
                            [styles.unsetMobileHeight]: unsetMobileHeight,
                        })}
                        data-tn="marketing-banner"
                    >
                        <MessageBannerContent
                            viewer={viewer}
                            user={user || null}
                            messages={messages}
                            showPreviewMessage={variables.hasPreviewVariables}
                            useGlobalFallbackMessage={useGlobalFallbackMessage}
                            onMessageDisplay={({ messageTitle }) =>
                                setCurrentMessageTitle(messageTitle)
                            }
                        />
                        {/* Disable banner close on desktop and tablet */}
                        {!variables.hasPreviewVariables && isMobile && (
                            <Link
                                className={styles.closeIconWrapper}
                                onClick={() => {
                                    setIsBannerClosedManuallyInStorage();
                                    setIsBannerClosedManually(true);
                                    onBannerClose();
                                    trackMessageBanner({
                                        currentMessageTitle,
                                        action: 'marketing banner closed',
                                        step_interaction_name:
                                            STEP_INTERACTION_GLOBAL_MESSAGE_BANNER_CLOSE,
                                    });
                                }}
                                ariaLabel="Close"
                            >
                                <Close className={styles.closeIcon} />
                            </Link>
                        )}
                    </div>
                );
            }}
        />
    );
}

MessageBanner.propTypes = {
    hasRootPendingRefetch: bool,
    hasUserId: bool,
    isClient: bool,
    unsetMobileHeight: bool,
    useGlobalFallbackMessage: bool,
    userId: string,
    relayEnvironment: object,
    onBannerClose: func,
};

export default MessageBanner;
